import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "@/components";
import { axiosInstance } from "@/helpers/axios";
import { CreateSubsceneDrawer } from "../create-subscene-drawer/CreateSubsceneDrawer.comp";
import styles from "./SubsceneManagerDrawer.module.scss";

type SubsceneRow = {
  subscene_id: string;
  subscene_key: string;
  display_name: string;
  subscene_version_id: string;
  version_label: string;
  parent_version_label: string;
  status: string;
  structure_keys_csv: string | null;
  processing_error: string | null;
  updated_at: number;
};

type SubsceneManagerDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onChanged?: () => void | Promise<void>;
  parentVersionId: string;
  parentVersionLabel: string;
};

export const SubsceneManagerDrawer = ({
  isOpen,
  onClose,
  onChanged,
  parentVersionId,
  parentVersionLabel,
}: SubsceneManagerDrawerProps) => {
  const [items, setItems] = useState<SubsceneRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newVersionFor, setNewVersionFor] = useState<SubsceneRow | null>(null);

  const load = useCallback(async () => {
    if (!parentVersionId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/v2/models/versions/${encodeURIComponent(parentVersionId)}/subscenes`
      );
      setItems((data?.data?.items ?? []) as SubsceneRow[]);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [parentVersionId]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  const latestBySubscene = items.reduce<Map<string, SubsceneRow>>((acc, row) => {
    const existing = acc.get(row.subscene_id);
    if (!existing || row.updated_at > existing.updated_at) {
      acc.set(row.subscene_id, row);
    }
    return acc;
  }, new Map());

  const displayRows = [...latestBySubscene.values()].sort((a, b) =>
    a.subscene_key.localeCompare(b.subscene_key)
  );

  const modelVersionLabel =
    displayRows[0]?.parent_version_label ?? parentVersionLabel;

  const activate = async (subsceneVersionId: string) => {
    setBusyId(subsceneVersionId);
    try {
      await axiosInstance.post(
        `/v2/models/subscene-versions/${encodeURIComponent(subsceneVersionId)}/activate`
      );
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const existingSubscene = useMemo(
    () =>
      newVersionFor
        ? {
            subsceneId: newVersionFor.subscene_id,
            subsceneKey: newVersionFor.subscene_key,
            displayName: newVersionFor.display_name,
            structureKeys: newVersionFor.structure_keys_csv
              ? newVersionFor.structure_keys_csv.split(",").filter(Boolean)
              : [],
          }
        : null,
    [newVersionFor],
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        ariaLabel="Manage subscenes"
        closeOnOutsideClick={!createOpen}
        closeOnEscape={!createOpen}
      >
        <DrawerHeader
          title={`Subscenes · model ${modelVersionLabel}`}
          onClose={onClose}
        />
        <DrawerBody>
          <div className={styles["comp"]}>
            <p className={styles["comp__hint"]}>
              Subscenes share the main model version. Each upload bumps the patch
              (e.g. v0.0.1 → v0.0.2). After processing, subscenes activate automatically.
              Publish the main model to the catalog so they appear in the app.
            </p>

            {loading ? (
              <p className={styles["comp__empty"]}>Loading…</p>
            ) : displayRows.length === 0 ? (
              <p className={styles["comp__empty"]}>
                No subscenes for this version yet.
              </p>
            ) : (
              <ul className={styles["comp__list"]}>
                {displayRows.map((row) => {
                  const keys = row.structure_keys_csv
                    ? row.structure_keys_csv.split(",").filter(Boolean)
                    : [];
                  return (
                    <li key={row.subscene_version_id} className={styles["comp__item"]}>
                      <div className={styles["comp__item-header"]}>
                        <strong>{row.display_name}</strong>
                        <span className={styles["comp__badge"]}>{row.status}</span>
                      </div>
                      <div className={styles["comp__meta"]}>
                        <code>{row.subscene_key}</code>
                      </div>
                      {keys.length > 0 ? (
                        <div className={styles["comp__keys"]}>
                          Structures: {keys.join(", ")}
                        </div>
                      ) : null}
                      {row.processing_error ? (
                        <div className={styles["comp__error"]}>{row.processing_error}</div>
                      ) : null}
                      <div className={styles["comp__actions"]}>
                        {row.status === "draft" ? (
                          <Button
                            size="small"
                            variant="primary"
                            disabled={busyId !== null}
                            onClick={() => void activate(row.subscene_version_id)}
                          >
                            {busyId === row.subscene_version_id ? "Activating…" : "Activate"}
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => {
                            setNewVersionFor(row);
                            setCreateOpen(true);
                          }}
                        >
                          New file
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setNewVersionFor(null);
              setCreateOpen(true);
            }}
          >
            Add subscene
          </Button>
        </DrawerFooter>
      </Drawer>

      <CreateSubsceneDrawer
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setNewVersionFor(null);
        }}
        onCreated={async () => {
          await load();
          await onChanged?.();
        }}
        parentVersionId={parentVersionId}
        existingSubscene={existingSubscene}
      />
    </>
  );
};
